import { TranslationKey } from 'app/core/i18n/translation-keys';
import { BPGroupingCriteria, BPGroupingSection } from '../types/bp-grouping.types';

/**
 * Label keys for BPGroupingCriteria enum. Resolved via `tEnum()` at render time — never cache the
 * resolved string, see the i18n pattern doc's Gotchas.
 */
export const BP_GROUPING_LABEL_KEYS: Record<BPGroupingCriteria, TranslationKey> = {
  [BPGroupingCriteria.NONE]: 'routing.SUB_ROUTE_PARTNERS.GROUPING.LABELS.NONE',
  [BPGroupingCriteria.BUSINESS_PARTNER_GROUP]: 'routing.SUB_ROUTE_PARTNERS.GROUPING.LABELS.BP_GROUP',
  [BPGroupingCriteria.BUSINESS_PARTNER_SUB_GROUP]: 'routing.SUB_ROUTE_PARTNERS.GROUPING.LABELS.BP_SUBGROUP',
  [BPGroupingCriteria.ROUTE]: 'routing.SUB_ROUTE_PARTNERS.GROUPING.LABELS.ROUTE',
  [BPGroupingCriteria.ROUTE_AREA]: 'routing.SUB_ROUTE_PARTNERS.GROUPING.LABELS.ROUTE_AREA',
  [BPGroupingCriteria.SALES_OFFICE]: 'routing.SUB_ROUTE_PARTNERS.GROUPING.LABELS.SALES_OFFICE',
  [BPGroupingCriteria.LOCATION_ONE]: 'routing.SUB_ROUTE_PARTNERS.GROUPING.LABELS.LOCATION_ONE',
  [BPGroupingCriteria.LOCATION_TWO]: 'routing.SUB_ROUTE_PARTNERS.GROUPING.LABELS.LOCATION_TWO',
  [BPGroupingCriteria.LOCATION_THREE]: 'routing.SUB_ROUTE_PARTNERS.GROUPING.LABELS.LOCATION_THREE',
  [BPGroupingCriteria.LOCATION_FOUR]: 'routing.SUB_ROUTE_PARTNERS.GROUPING.LABELS.LOCATION_FOUR',
};

/**
 * Short label keys for button text
 */
export const BP_GROUPING_SHORT_LABEL_KEYS: Record<BPGroupingCriteria, TranslationKey> = {
  [BPGroupingCriteria.NONE]: 'routing.SUB_ROUTE_PARTNERS.GROUPING.SHORT_LABELS.NONE',
  [BPGroupingCriteria.BUSINESS_PARTNER_GROUP]: 'routing.SUB_ROUTE_PARTNERS.GROUPING.SHORT_LABELS.BP_GROUP',
  [BPGroupingCriteria.BUSINESS_PARTNER_SUB_GROUP]: 'routing.SUB_ROUTE_PARTNERS.GROUPING.SHORT_LABELS.BP_SUBGROUP',
  [BPGroupingCriteria.ROUTE]: 'routing.SUB_ROUTE_PARTNERS.GROUPING.SHORT_LABELS.ROUTE',
  [BPGroupingCriteria.ROUTE_AREA]: 'routing.SUB_ROUTE_PARTNERS.GROUPING.SHORT_LABELS.ROUTE_AREA',
  [BPGroupingCriteria.SALES_OFFICE]: 'routing.SUB_ROUTE_PARTNERS.GROUPING.SHORT_LABELS.SALES_OFFICE',
  [BPGroupingCriteria.LOCATION_ONE]: 'routing.SUB_ROUTE_PARTNERS.GROUPING.SHORT_LABELS.LOCATION_ONE',
  [BPGroupingCriteria.LOCATION_TWO]: 'routing.SUB_ROUTE_PARTNERS.GROUPING.SHORT_LABELS.LOCATION_TWO',
  [BPGroupingCriteria.LOCATION_THREE]: 'routing.SUB_ROUTE_PARTNERS.GROUPING.SHORT_LABELS.LOCATION_THREE',
  [BPGroupingCriteria.LOCATION_FOUR]: 'routing.SUB_ROUTE_PARTNERS.GROUPING.SHORT_LABELS.LOCATION_FOUR',
};

/**
 * Icons for each grouping criteria — icon names, not user-facing copy.
 */
export const BP_GROUPING_ICONS: Record<BPGroupingCriteria, string> = {
  [BPGroupingCriteria.NONE]: 'view_list',
  [BPGroupingCriteria.BUSINESS_PARTNER_GROUP]: 'group_work',
  [BPGroupingCriteria.BUSINESS_PARTNER_SUB_GROUP]: 'groups',
  [BPGroupingCriteria.ROUTE]: 'alt_route',
  [BPGroupingCriteria.ROUTE_AREA]: 'map',
  [BPGroupingCriteria.SALES_OFFICE]: 'business',
  [BPGroupingCriteria.LOCATION_ONE]: 'public',
  [BPGroupingCriteria.LOCATION_TWO]: 'location_city',
  [BPGroupingCriteria.LOCATION_THREE]: 'domain',
  [BPGroupingCriteria.LOCATION_FOUR]: 'place',
};

/**
 * Tooltip label keys for each grouping criteria
 */
export const BP_GROUPING_TOOLTIP_KEYS: Record<BPGroupingCriteria, TranslationKey> = {
  [BPGroupingCriteria.NONE]: 'routing.SUB_ROUTE_PARTNERS.GROUPING.TOOLTIPS.NONE',
  [BPGroupingCriteria.BUSINESS_PARTNER_GROUP]: 'routing.SUB_ROUTE_PARTNERS.GROUPING.TOOLTIPS.BP_GROUP',
  [BPGroupingCriteria.BUSINESS_PARTNER_SUB_GROUP]: 'routing.SUB_ROUTE_PARTNERS.GROUPING.TOOLTIPS.BP_SUBGROUP',
  [BPGroupingCriteria.ROUTE]: 'routing.SUB_ROUTE_PARTNERS.GROUPING.TOOLTIPS.ROUTE',
  [BPGroupingCriteria.ROUTE_AREA]: 'routing.SUB_ROUTE_PARTNERS.GROUPING.TOOLTIPS.ROUTE_AREA',
  [BPGroupingCriteria.SALES_OFFICE]: 'routing.SUB_ROUTE_PARTNERS.GROUPING.TOOLTIPS.SALES_OFFICE',
  [BPGroupingCriteria.LOCATION_ONE]: 'routing.SUB_ROUTE_PARTNERS.GROUPING.TOOLTIPS.LOCATION_ONE',
  [BPGroupingCriteria.LOCATION_TWO]: 'routing.SUB_ROUTE_PARTNERS.GROUPING.TOOLTIPS.LOCATION_TWO',
  [BPGroupingCriteria.LOCATION_THREE]: 'routing.SUB_ROUTE_PARTNERS.GROUPING.TOOLTIPS.LOCATION_THREE',
  [BPGroupingCriteria.LOCATION_FOUR]: 'routing.SUB_ROUTE_PARTNERS.GROUPING.TOOLTIPS.LOCATION_FOUR',
};

/**
 * Section categories for the grouping menu. `name` is a compile-checked TranslationKey (not a raw
 * string), so resolving it with `t(section.name)` at the call site is safe.
 */
export const BP_GROUPING_SECTIONS: BPGroupingSection[] = [
  {
    name: 'routing.SUB_ROUTE_PARTNERS.GROUPING.SECTIONS.CLIENT',
    icon: 'business_center',
    options: [
      BPGroupingCriteria.BUSINESS_PARTNER_GROUP,
      BPGroupingCriteria.BUSINESS_PARTNER_SUB_GROUP,
    ],
  },
  {
    name: 'routing.SUB_ROUTE_PARTNERS.GROUPING.SECTIONS.ROUTES',
    icon: 'route',
    options: [BPGroupingCriteria.ROUTE, BPGroupingCriteria.ROUTE_AREA],
  },
  {
    name: 'routing.SUB_ROUTE_PARTNERS.GROUPING.SECTIONS.SALES',
    icon: 'store',
    options: [BPGroupingCriteria.SALES_OFFICE],
  },
  {
    name: 'routing.SUB_ROUTE_PARTNERS.GROUPING.SECTIONS.LOCATION',
    icon: 'location_on',
    options: [
      BPGroupingCriteria.LOCATION_ONE,
      BPGroupingCriteria.LOCATION_TWO,
      BPGroupingCriteria.LOCATION_THREE,
      BPGroupingCriteria.LOCATION_FOUR,
    ],
  },
];
